import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import {
    AttemptType,
    AttemptWithAnswers,
    CreateAttemptResponse,
    UpdateAttemptResponse,
    AnswerSubmission,
    TopikResponse,
    TopikListResponse
} from './types';

/**
 * List user's TOPIK exam attempts
 * @param params Object with filtering and pagination options
 * @returns Promise with array of attempts
 */
export const listAttempts = async (params?: {
    userid?: string;
    examid?: string;
    status?: string;
    limit?: number;
    offset?: number;
}): Promise<AttemptType[] | null> => {
    try {
        const res = await api.get<TopikListResponse<AttemptType>>('/topik/attempt', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? []) : null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.code === 404) {
            console.warn('No attempts found for the given parameters');
            return [];
        }

        console.error('Failed to list attempts:', e);
        return null;
    }
};

/**
 * Get specific attempt with all submitted answers
 * @param attemptid Attempt ID to retrieve
 * @returns Promise with attempt and answers or null if not found
 */
export const getAttempt = async (attemptid: string): Promise<AttemptWithAnswers | null> => {
    try {
        const res = await api.get<TopikResponse<AttemptWithAnswers>>('/topik/attempt', {
            params: { attemptid },
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error(`Failed to get attempt ${attemptid}:`, e);
        return null;
    }
};

/**
 * Create a new TOPIK exam attempt
 * @param params Object with examid or questionnaireid, and optional section
 * @returns Promise with created attempt or null if failed
 */
export const createAttempt = async (params: {
    examid?: string;
    questionnaireid?: string;
    userid?: string;
    section?: 'listening' | 'reading' | 'writing';
}): Promise<CreateAttemptResponse | null> => {
    try {
        const res = await api.post<TopikResponse<CreateAttemptResponse>>('/topik/attempt', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to create attempt:', e);
        return null;
    }
};

/**
 * Update attempt (start or submit)
 * @param params Object with attemptid and action
 * @returns Promise with updated attempt or null if failed
 */
export const updateAttempt = async (params: {
    attemptid: string;
    action?: 'start' | 'submit';
    answers?: AnswerSubmission[];
}): Promise<UpdateAttemptResponse | null> => {
    try {
        const res = await api.put<TopikResponse<UpdateAttemptResponse>>('/topik/attempt', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to update attempt:', e);
        return null;
    }
};

/**
 * Alias for updateAttempt using PATCH method
 */
export const patchAttempt = async (params: {
    attemptid: string;
    action?: 'start' | 'submit';
    answers?: AnswerSubmission[];
}): Promise<UpdateAttemptResponse | null> => {
    try {
        const res = await api.patch<TopikResponse<UpdateAttemptResponse>>('/topik/attempt', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to patch attempt:', e);
        return null;
    }
};

/**
 * Start an attempt
 * @param attemptid The attempt ID to start
 * @returns Promise with updated attempt or null if failed
 */
export const startAttempt = async (attemptid: string): Promise<UpdateAttemptResponse | null> => {
    return updateAttempt({ attemptid, action: 'start' });
};

/**
 * Submit an attempt with answers
 * @param attemptid The attempt ID to submit
 * @param answers Optional array of answers to submit
 * @returns Promise with updated attempt or null if failed
 */
export const submitAttempt = async (
    attemptid: string, 
    answers?: AnswerSubmission[]
): Promise<UpdateAttemptResponse | null> => {
    return updateAttempt({ attemptid, action: 'submit', answers });
};